import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_ACCURACY_METERS,
  nearestSite,
  type CheckState,
  type SitePoint,
} from '@goyone/shared';
import { api } from '../lib/api';
import { time } from '../lib/format';
import { Icon } from './Icon';
import { Modal } from './ui';
import { useToast } from './Toast';

interface DayState {
  linked: boolean;
  date?: string;
  state?: CheckState;
  stateLabel?: string;
  stateKind?: string;
  action?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  lateMinutes?: number;
  workdayStart?: string;
  workdayEnd?: string;
  sites?: SitePoint[];
  geofenced?: boolean;
  maxAccuracy?: number;
}

/**
 * زرُّ الحضور والانصراف في الشريط العلويّ.
 *
 * في الشريط لا في شاشة: من يأتي صباحًا يفتح النظام على شاشةِ عمله لا على
 * شاشةِ الحضور، ومن ينصرف يغلق ما بين يديه — وزرٌّ في صفحةٍ لا يُفتح
 * زرٌّ لا يُضغط. وهو يظهر لمن له ملفٌّ وظيفيٌّ فقط: من لا ملفَّ له لا
 * حضورَ يُسجَّل عليه.
 */
export function CheckClock() {
  const [open, setOpen] = useState(false);

  const day = useQuery({
    queryKey: ['hr', 'me', 'day'],
    queryFn: () => api<DayState>('/hr/me/day'),
    /* دقيقةٌ تكفي: الحالةُ لا تتغيّر إلّا بفعلٍ من صاحبها. */
    staleTime: 60_000,
    retry: false,
  });

  const d = day.data;
  if (!d?.linked || !d.state) return null;

  const done = d.state === 'DONE';
  const inNow = d.state === 'IN';

  return (
    <>
      <button
        className={`clock-btn clock-${d.stateKind}`}
        onClick={() => setOpen(true)}
        title={
          inNow
            ? `حضرتَ ${time(d.checkIn)}`
            : done
              ? `انصرفتَ ${time(d.checkOut)}`
              : (d.stateLabel ?? '')
        }
      >
        <Icon name="clock" size={15} />
        <span className="clock-text">
          {d.action ?? d.stateLabel}
        </span>
        {inNow && <span className="clock-dot" aria-hidden="true" />}
      </button>

      {open && <ClockDialog day={d} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * لوحةُ التسجيل.
 *
 * الموقعُ يُطلب عند فتحها لا عند الضغط: الإذنُ يستغرق ثوانيَ ويظهر فيها
 * نافذةُ المتصفّح، ومن يضغط «سجّل» يريد أن يُسجَّل الآن لا أن ينتظر.
 * والمسافةُ تُعرض قبل الضغط فلا يضغط ليُردّ.
 */
function ClockDialog({ day, onClose }: { day: DayState; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [fix, setFix] = useState<GeolocationPosition | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');

  const sites = day.sites ?? [];
  const inNow = day.state === 'IN';
  const done = day.state === 'DONE';

  const ask = () => {
    if (!('geolocation' in navigator)) {
      setGeoError('متصفّحك لا يعطي الموقع. افتح النظام من متصفّحٍ حديث.');
      return;
    }
    setAsking(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setFix(p);
        setAsking(false);
      },
      (e) => {
        setAsking(false);
        /*
         * الرسالةُ تقول ما يفعله المستخدم لا رقمَ الخطأ.
         *
         * والحالةُ الأولى — رفضُ الإذن — هي الأكثر وقوعًا، وحلُّها في
         * إعدادات الموقع بالمتصفّح لا في النظام.
         */
        setGeoError(
          e.code === e.PERMISSION_DENIED
            ? 'رفضتَ إذن الموقع. افتح إعدادات الموقع في متصفّحك واسمح لهذا الموقع ثمّ أعد المحاولة.'
            : e.code === e.POSITION_UNAVAILABLE
              ? 'تعذّر تحديد موقعك الآن. اخرج إلى مكانٍ مكشوف وأعد المحاولة.'
              : 'استغرق تحديد الموقع وقتًا طويلًا. أعد المحاولة.',
        );
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
  };

  /* الطلبُ مرّةً عند الفتح، ثمّ بزرٍّ إن أخفق. */
  useEffect(() => {
    if (!done) ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = fix
    ? { lat: fix.coords.latitude, lng: fix.coords.longitude }
    : null;
  const accuracy = fix ? Math.round(fix.coords.accuracy) : null;
  const match = point && sites.length > 0 ? nearestSite(point, sites) : null;

  const accuracyBad =
    accuracy !== null && accuracy > (day.maxAccuracy ?? MAX_ACCURACY_METERS);
  const ready =
    Boolean(point) && !accuracyBad && (sites.length === 0 || match?.inside);

  const send = useMutation({
    mutationFn: () =>
      api<{
        at: string;
        lateMinutes?: number;
        overtimeMinutes?: number;
        earlyMinutes?: number;
        /** ما لم يُتحقَّق منه — يُقال للموظّف ولا يُسكَت عنه. */
        note?: string | null;
      }>(
        inNow ? '/hr/me/check-out' : '/hr/me/check-in',
        {
          method: 'POST',
          body: {
            lat: point!.lat,
            lng: point!.lng,
            accuracy: accuracy ?? 0,
            note: note.trim() || null,
          },
        },
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      toast(
        inNow
          ? r.earlyMinutes
            ? `سُجّل انصرافك ${time(r.at)} — قبل الموعد بـ ${r.earlyMinutes} دقيقة`
            : `سُجّل انصرافك ${time(r.at)}`
          : r.lateMinutes
            ? `سُجّل حضورك ${time(r.at)} — تأخير ${r.lateMinutes} دقيقة`
            : `سُجّل حضورك ${time(r.at)}`,
      );
      /*
       * وما لم يُتحقَّق منه يُقال في رسالةٍ ثانية.
       *
       * جمعُه مع رسالة النجاح يجعله يُقرأ جزءًا من التأكيد فيمرّ — وهو
       * تحفّظٌ على التأكيد لا زينةٌ فيه.
       */
      if (r.note) toast(r.note);
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التسجيل'),
  });

  return (
    <Modal
      title={inNow ? 'تسجيل الانصراف' : 'تسجيل الحضور'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إغلاق
          </button>
          {!done && (
            <button
              className="btn btn-primary"
              onClick={() => send.mutate()}
              disabled={!ready || send.isPending}
            >
              {send.isPending
                ? 'جارٍ التسجيل'
                : inNow
                  ? 'سجّل انصرافي'
                  : 'سجّل حضوري'}
            </button>
          )}
        </>
      }
    >
      <div className="clock-now">
        <div>
          <span className="tiny mute">الحضور</span>
          <strong className="num">{day.checkIn ? time(day.checkIn) : '—'}</strong>
        </div>
        <div>
          <span className="tiny mute">الانصراف</span>
          <strong className="num">
            {day.checkOut ? time(day.checkOut) : '—'}
          </strong>
        </div>
        <div>
          <span className="tiny mute">الدوام</span>
          <strong className="num">
            {day.workdayStart} — {day.workdayEnd}
          </strong>
        </div>
      </div>

      {done && (
        <p className="tiny mute mt-4">
          سجّلتَ حضورك وانصرافك اليوم. وما بقي من تصحيحٍ يمرّ على مديرك من
          ورقة الحضور.
        </p>
      )}

      {!done && (
        <div className="mt-4">
          {geoError && (
            <>
              <p className="tiny" style={{ color: 'var(--danger)' }}>
                {geoError}
              </p>
              <button className="btn btn-sm mt-2" onClick={ask} disabled={asking}>
                <Icon name="refresh" size={14} />
                أعد المحاولة
              </button>
            </>
          )}

          {!geoError && asking && (
            <p className="tiny mute">جارٍ تحديد موقعك…</p>
          )}

          {point && (
            <>
              {sites.length === 0 ? (
                /*
                 * بلا مواقعَ مضبوطةٍ يُقبل التسجيل ولا يُدَّعى تحقّقٌ لم يقع.
                 * والرسالةُ تقول لمن يقرأ لماذا: الإعدادُ ناقصٌ لا النظام.
                 */
                <p className="tiny mute">
                  لا مواقع عملٍ مضبوطةٌ في النظام، فيُسجَّل حضورُك بلا تحقّقٍ
                  من المكان. تُضبط المواقع من الإعدادات.
                </p>
              ) : match?.inside ? (
                <p className="tiny" style={{ color: 'var(--success)' }}>
                  أنت في «{match.site.name}» — على بُعد {match.meters} مترًا من
                  مركزه.
                </p>
              ) : match ? (
                <p className="tiny" style={{ color: 'var(--danger)' }}>
                  أنت خارج مواقع العمل. أقربُها «{match.site.name}» على بُعد{' '}
                  {match.meters} مترًا، ونطاقُه {match.site.radiusMeters} مترًا.
                </p>
              ) : null}

              {accuracyBad && (
                <p className="tiny" style={{ color: 'var(--danger)' }}>
                  دقّة التحديد ±{accuracy} متر، وهي أضعف من أن يُبنى عليها.
                  اخرج إلى مكانٍ مكشوفٍ أو فعّل الموقع الدقيق.
                </p>
              )}

              {!accuracyBad && accuracy !== null && (
                <p className="tiny mute">دقّة التحديد ±{accuracy} متر.</p>
              )}
            </>
          )}

          <div className="field mt-4">
            <label className="label" htmlFor="clock-note">
              ملاحظة
            </label>
            <input
              id="clock-note"
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="اختيارية — مثل: زيارة عميل."
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
